<template>
  <div class="checklist-inspection-right-pane">
    <Transition name="checklist-inspection-pane-overlay">
      <div
        v-if="open"
        class="checklist-inspection-right-pane-overlay"
        aria-hidden="true"
        @click="$emit('close')"
      />
    </Transition>
    <Transition name="checklist-inspection-pane-slide">
      <div
        v-if="open"
        class="checklist-inspection-right-pane-wrapper"
        role="dialog"
        aria-label="Checklist inspection pane"
      >
        <div class="checklist-inspection-right-pane-header-container">
          <div class="checklist-inspection-right-pane-header-info">
            <div class="checklist-inspection-right-pane-header-title">Inspect employee</div>
            <div class="inspection-employee-card">
              <v-avatar size="32">
                <v-img v-if="employee?.photoUrl" :src="employee?.photoUrl" :alt="employee?.fullName" />
                <span v-else class="avatar-placeholder">{{ employeeInitials }}</span>
              </v-avatar>
              <div class="inspection-employee-card-info">
                <span class="inspection-employee-card-info-name">{{
                  employee?.fullName
                }}</span>
                <span class="inspection-employee-card-info-position">{{
                  employee?.position
                }}</span>
              </div>
            </div>
          </div>
          <div class="checklist-inspection-right-pane-close-container">
            <button
              type="button"
              class="checklist-inspection-right-pane-close-button"
              aria-label="Close pane"
              @click="$emit('close')"
            >
                <Icon name="popup_close" size="small" />
            </button>
          </div>
        </div>
        <div class="checklist-inspection-right-pane-responsibility-header">
          <div class="checklist-inspection-right-pane-responsibility-header-text">
            On Responsibility:
          </div>
          <div v-if="isResponsibilityAssignment" class="checklist-inspection-right-pane-responsibility-dropdown">
            <ResponsibilityAssignmentDropDown
              :responsibilityCode="responsibilityAssignment?.responsibilityCode ?? ''"
              :responsibilityName="responsibilityAssignment?.responsibilityName ?? ''"
              :responsibilityImportance="responsibilityAssignment?.responsibilityImportance ?? 2"
              :roleCode="responsibilityAssignment?.roleCode ?? ''"
              :roleName="responsibilityAssignment?.roleName ?? ''"
              :roleImportance="responsibilityAssignment?.roleImportance ?? 2"
              :positionCode="responsibilityAssignment?.positionCode ?? ''"
              :positionName="responsibilityAssignment?.positionName ?? ''"
              :positionImportance="responsibilityAssignment?.positionImportance ?? 2"
              :positionCommitment="responsibilityAssignment?.positionCommitment ?? 'Primary'"
              :assignedOn="responsibilityAssignment?.assignedOn"
              :newDuration="responsibilityAssignment?.newDuration"
              :show_codes="responsibilityAssignment?.show_codes !== false"
            />
          </div>
          <div v-else class="checklist-inspection-right-pane-responsibility-card">
            <OrgObjectCard
              type="responsibility"
              :codeValue="responsibilityAssignment?.responsibilityCode ?? ''"
              :name="responsibilityAssignment?.responsibilityName ?? ''"
              :code="true"
              :icon="true"
              :allow_hover="false"
              state="Active"
            />
          </div>
          <ReadinessBadge
            :key="readinessChipKey"
            :badge-id="currentBadgeIdForDropdown"
            :id="currentBadgeIdForDropdown"
            :hideChatterIcon="true"
            :level="readinessChipLevel"
            :dot="readinessChipDot"
            view="Editable"
            :expiration_date="readinessBadgeExpirationDate"
            :expires_in_days_from_becoming_obsolete="readinessBadgeExpiresInDaysFromObsolete"
            :has_expiration_if_obsolete="readinessBadgeHasExpirationIfObsolete"
            @removeBadge="onRemoveBadge"
            @expiration-synced="emit('expirationSynced')"
          />
        </div>
        <div class="checklist-inspection-right-pane-body">
            <ChecklistInspectionTab v-show="!isCheckpointInspection && !showCheckpointView" :items="itemsWithPrefix" :currentBadgeLevel="readinessChipLevel" :currentBadgeId="currentBadgeIdForDropdown" :resetFilterToNotYetPassedFilterCount="switchToTableWithNotYetPassedFilterCount" :hasActiveCheckpoints="paneHasActiveCheckpoints" @onInspect="onInspect" @issueBadge="onIssueBadge" @filterChanged="onFilterChanged" @onClose="(row) => emit('onClose', row)" @filteredCheckpointIdsChanged="onFilteredCheckpointIdsChanged" @onChatterClick="onCheckpointChatterClick" />
            <Transition name="checkpoint-slide">
              <CheckpointInspectionRightPane
              v-if="isCheckpointInspection || showCheckpointView"
              @showChecklistInspectionTab="onShowChecklistInspectionTab"
              @next="localCurrentCheckpoint++"
              @previous="localCurrentCheckpoint--"
              @onPass="handleOnPass"
              @onFail="handleOnFail"
              @onNotApplicable="onNotApplicableFromNavigator"
              @onClose="emit('onClose', currentCheckpointItem)"
              :checkpoints="activeCheckpoints"
              :currentCheckpoint="localCurrentCheckpoint"
              :evaluationItem="currentCheckpointItem"
              :hasFilter="localHasFilter"
            />
            </Transition>
        </div>
        <AwardBadgeDialog
          :isVisible="showAwardBadgeDialog"
          :employee="{ name: employee?.fullName ?? '', avatar: employee?.photoUrl ?? '' }"
          :level="awardBadgeLevel"
          :currentEmployee="currentEmployee"
          :eligibility-by-access-null="awardEligibilityByAccessNull"
          :use-verification-notify-copy="awardUseVerificationNotifyCopy"
          :responsibilityName="responsibilityAssignment?.responsibilityName ?? ''"
          content-class="checklist-inspection-award-dialog"
          @close="showAwardBadgeDialog = false"
          @skip="showAwardBadgeDialog = false"
          @activate="onAwardBadge"
        />
        <ChatterDrawer
          :drawer="chatterDrawerOpen"
          :type="chatterTypeName"
          :typeValue="chatterTypeValue"
          :listData="chatterListData"
          object-type="checkpoint_evaluation"
          :object-id="recordId"
          :chatterList="chatterList"
          :totalCheckpointChatter="totalChatter"
          iconName="org_icon_responsibility"
          @closeDrawer="closeChatterDrawer"
          @requestMoreCheckpointsChatter="loadMoreChatter"
        />
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { normalizeBadgeLevel } from "@components/utils/normalizeBadgeLevel";
import { computed, ref, watch, nextTick, getCurrentInstance } from "vue";
import { createProficiencyBadge } from "@/api";
import { extractCreatedProficiencyBadgeId } from "@/utils/proficiencyBadgeHelpers";
import Icon from "@components/icons/Icon.vue";
import ResponsibilityAssignmentDropDown from "@components/ResponsibilityAssignmentDropDown/ResponsibilityAssignmentDropDown.vue";
import OrgObjectCard from "@components/OrgObjectCard/OrgObjectCard.vue";
import ReadinessBadge from "../ReadinessBadge/ReadinessBadge.vue";
import ChecklistInspectionTab from "../ChecklistInspectionTab/ChecklistInspectionTab.vue";
import { resolveHasActiveCheckpoints } from "@components/EvaluationGrandBadge/badgeIssueRules";
import CheckpointInspectionRightPane from "@components/CheckpointInspectionRightPane/CheckpointInspectionRightPane.vue";
import AwardBadgeDialog from "@components/AwardBadgeDialog/AwardBadgeDialog.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import {
  fetchCheckpointEvaluationChatter,
  resolveAccountIdFromStorage,
  resolveCheckpointEvaluationRecordId,
} from "@components/utils/checkpointEvaluationChatter";

const props = defineProps({
  open: {
    type: Boolean,
    default: true,
  },
  employee: {
    type: Object,
    default: () => ({
      fullName: "John Doe",
      photoUrl: "https://thispersondoesnotexist.com/",
      position: "Senior Software Engineer",
      badge: {
        level: "Apprentice",
        dot: "Good Standing",
        id: 1
      }
    }),
  },
  responsibilityAssignment: {
    type: Object,
    default: () => ({
      responsibilityCode: "#9999",
      responsibilityName: "Responsibility Name",
      responsibilityImportance: 2,
      roleCode: "#9999",
      roleName: "Role Name",
      roleImportance: 2,
      positionCode: "#9999",
      positionName: "Position Name",
      positionImportance: 2,
      positionCommitment: "Primary",
      assignedOn: new Date("2025-09-01"),
      newDuration: 15,
      show_codes: true
    }),
  },
  items: {
    type: Array,
    default: () => [],
  },
  isCheckpointInspection: {
    type: Boolean,
    default: false,
  },
  responsibilityCheckpoints: {
    type: Array,
    default: () => [],
  },
  currentEmployee: {
    type: Object,
    default: () => ({}),
  },
  /** Optional { level, dot, id } from parent — avoids nested `employee` reactivity issues (e.g. Vue 2 host). */
  readinessChip: {
    type: Object,
    default: null,
  },
  /** Parent sets `{ token, level }` to open AwardBadgeDialog without closing the pane (Path to Readiness). */
  awardDialogRequest: {
    type: Object,
    default: null,
  },
  /** Path to Readiness: AwardBadgeDialog uses `access == null` vs email notify. */
  awardEligibilityByAccessNull: {
    type: Boolean,
    default: false,
  },
  /**
   * When true (As a Coach): show “needs to be verified” when access is null.
   * Path to Readiness and other hosts omit this prop (default false) — original copy unchanged.
   */
  awardUseVerificationNotifyCopy: {
    type: Boolean,
    default: false,
  },
  /** Parent-provided Rule 13 flag; when omitted the pane derives it from its checkpoints. */
  hasActiveCheckpoints: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "close",
  "removeBadge",
  "issueBadge",
  "onPass",
  "onFail",
  "onNotApplicable",
  "onClose",
  "badgeAwarded",
  "expirationSynced",
]);

const { proxy } = getCurrentInstance();

const LEVEL_TO_INT = { apprentice: 1, proficient: 2, coach: 3, architect: 4 };
const AWARD_SUCCESS_TOAST_MS = 2500;

const showCheckpointView = ref(false);
const localHasFilter = ref(false);
const localCurrentCheckpoint = ref(0);
const filteredCheckpointIds = ref([]);
const switchToTableWithNotYetPassedFilterCount = ref(0);
const showAwardBadgeDialog = ref(false);
const awardBadgeLevel = ref("apprentice");
/** Local badge override so ReadinessBadge updates in real time when user awards or parent updates after upgrade. */
const displayBadge = ref(null);

/* Checkpoint chatter right panel state (mirrors AsACoachCheckpointInspectionCard). */
const chatterDrawerOpen = ref(false);
const chatterList = ref([]);
const totalChatter = ref(0);
const chatterPage = ref(1);
const chatterLoading = ref(false);
const chatterTypeName = ref("Checkpoint");
const chatterTypeValue = ref("");
const chatterListData = ref({});

const buildCheckpointChatterTitle = (row) => {
  const prefix = String(row?.prefix ?? "").trim();
  const criteria = String(row?.checkpoint ?? row?.criteria ?? "").trim();
  return prefix ? `${prefix} ${criteria}` : criteria;
};

const fetchChatter = async (page = 1, append = false) => {
  const accountId = resolveAccountIdFromStorage(null);
  const recordId = resolveCheckpointEvaluationRecordId(chatterListData.value);
  if (accountId == null || recordId == null || chatterLoading.value) return;

  chatterLoading.value = true;
  try {
    const { results, count } = await fetchCheckpointEvaluationChatter(accountId, recordId, page);
    totalChatter.value = count;
    chatterList.value = append ? [...chatterList.value, ...results] : results;
    chatterPage.value = page;
  } catch (error) {
    console.error("Error loading checkpoint evaluation chatter:", error);
  } finally {
    chatterLoading.value = false;
  }
};

const onCheckpointChatterClick = async (row) => {
  if (chatterLoading.value) return;

  chatterTypeValue.value = buildCheckpointChatterTitle(row);
  chatterListData.value = {
    id: resolveCheckpointEvaluationRecordId(row),
    criteria: String(row?.checkpoint ?? row?.criteria ?? ""),
  };
  chatterList.value = [];
  totalChatter.value = 0;
  chatterPage.value = 1;
  await fetchChatter(1, false);
  chatterDrawerOpen.value = true;
};

const loadMoreChatter = async () => {
  if (totalChatter.value <= chatterList.value.length) return;
  await fetchChatter(chatterPage.value + 1, true);
};

const closeChatterDrawer = () => {
  chatterDrawerOpen.value = false;
  chatterList.value = [];
  totalChatter.value = 0;
  chatterPage.value = 1;
  chatterListData.value = {};
};

const activeCheckpoints = computed(() => {
  if (!filteredCheckpointIds.value.length) return props.responsibilityCheckpoints;
  return filteredCheckpointIds.value
    .map((id) => props.responsibilityCheckpoints.find((cp) => cp.id === id))
    .filter(Boolean);
});

/** Rule 13: true whenever this responsibility has active checkpoints. */
const paneHasActiveCheckpoints = computed(() => {
  if (props.hasActiveCheckpoints) return true;
  if (
    resolveHasActiveCheckpoints({
      checkpoints: props.responsibilityCheckpoints,
    })
  ) {
    return true;
  }
  // The pane only opens for a responsibility with an active checklist, so a
  // non-empty checkpoint/evaluation list means active checkpoints exist even
  // when the records do not carry an explicit `status`.
  return (
    (props.responsibilityCheckpoints?.length || 0) > 0 ||
    (props.items?.length || 0) > 0
  );
});

/** Fill missing prefix from responsibilityCheckpoints so table and navigator show it everywhere. */
const itemsWithPrefix = computed(() => {
  const items = props.items || [];
  const cps = props.responsibilityCheckpoints || [];
  if (!cps.length) return items;
  return items.map((item) => {
    const hasPrefix = (item.prefix || "").toString().trim();
    if (hasPrefix) return item;
    const cp = cps.find(
      (c) => c.id === item.checkpoint_id || String(c.id) === String(item.checkpoint_id)
    );
    const prefix = (cp?.prefix || "").toString().trim();
    return prefix ? { ...item, prefix } : item;
  });
});

const currentCheckpointItem = computed(() => {
  const currentCp = activeCheckpoints.value[localCurrentCheckpoint.value];
  if (!currentCp) return null;
  return itemsWithPrefix.value.find((item) => item.checkpoint_id === currentCp.id) ?? null;
});

function normalizeProficiencyLevelForChip(raw) {
  if (raw == null || raw === "") return "Novice";
  if (typeof raw === "number" && raw >= 1 && raw <= 4) {
    return ["Apprentice", "Professional", "Coach", "Master"][raw - 1];
  }
  const s = String(raw).trim();
  const n = Number(s);
  if (!Number.isNaN(n) && n >= 1 && n <= 4) {
    return ["Apprentice", "Proficient", "Coach", "Architect"][n - 1];
  }
  const lower = normalizeBadgeLevel(s) ?? s.toLowerCase();
  const map = {
    apprentice: "Apprentice",
    proficient: "Proficient",
    coach: "Coach",
    architect: "Architect",
    novice: "Novice",
  };
  if (map[lower]) return map[lower];
  return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "Novice";
}

function normalizeDotForChip(raw) {
  const allowed = ["Good Standing", "Given", "Obsolete", "No Dot"];
  if (raw && allowed.includes(raw)) return raw;
  const c = String(raw || "")
    .toLowerCase()
    .replace(/_/g, " ");
  if (c === "good standing") return "Good Standing";
  if (c === "given") return "Given";
  if (c === "obsolete") return "Obsolete";
  return "Good Standing";
}

function proficiencyRank(label) {
  const m = {
    Novice: 0,
    Apprentice: 1,
    Professional: 2,
    Coach: 3,
    Master: 4,
  };
  return m[label] ?? 0;
}

function mirrorEmployeeBadgeIntoDisplay() {
  if (!props.open) return;
  const snap = props.readinessChip;
  if (snap != null && typeof snap === "object" && snap.level != null && snap.level !== "") {
    displayBadge.value = {
      level: normalizeProficiencyLevelForChip(snap.level),
      dot: normalizeDotForChip(snap.dot),
      id: snap.id,
    };
    return;
  }
  const b = props.employee?.badge;
  if (!b) {
    displayBadge.value = null;
    return;
  }
  displayBadge.value = {
    level: normalizeProficiencyLevelForChip(b.level),
    dot: normalizeDotForChip(b.dot),
    id: b.id,
  };
}

/**
 * `readinessChip` from parent wins when set (Path to Readiness / Vue 2 host).
 * Else employee.badge + displayBadge merge for Storybook / other hosts.
 */
const readinessChipLevel = computed(() => {
  const snap = props.readinessChip;
  const b = props.employee?.badge;
  const d = displayBadge.value;
  const snapLevel =
    snap != null && typeof snap === "object" && snap.level != null && snap.level !== ""
      ? normalizeProficiencyLevelForChip(snap.level)
      : null;
  const bLevel =
    b?.level != null && b.level !== ""
      ? normalizeProficiencyLevelForChip(b.level)
      : null;
  const dLevel =
    d?.level != null && d.level !== ""
      ? normalizeProficiencyLevelForChip(d.level)
      : null;
  // Local pane updated (award/API) before Vue 2 parent readinessChip catches up
  if (dLevel != null && snapLevel != null && proficiencyRank(dLevel) > proficiencyRank(snapLevel)) {
    return dLevel;
  }
  if (snapLevel != null) {
    return snapLevel;
  }
  if (dLevel != null && bLevel != null && dLevel !== bLevel) {
    return dLevel;
  }
  if (bLevel != null) return bLevel;
  return dLevel ?? "Novice";
});

const readinessChipDot = computed(() => {
  const snap = props.readinessChip;
  const b = props.employee?.badge;
  const d = displayBadge.value;
  const snapLevel =
    snap != null && typeof snap === "object" && snap.level != null && snap.level !== ""
      ? normalizeProficiencyLevelForChip(snap.level)
      : null;
  const dLevel =
    d?.level != null && d.level !== ""
      ? normalizeProficiencyLevelForChip(d.level)
      : null;
  if (dLevel != null && snapLevel != null && proficiencyRank(dLevel) > proficiencyRank(snapLevel)) {
    return d?.dot != null ? normalizeDotForChip(d.dot) : "Good Standing";
  }
  if (snap != null && typeof snap === "object" && snap.dot != null && snap.dot !== "") {
    return normalizeDotForChip(snap.dot);
  }
  const bDot = b?.dot != null ? normalizeDotForChip(b.dot) : null;
  const dDot = d?.dot != null ? normalizeDotForChip(d.dot) : null;
  if (dDot != null && bDot != null && dDot !== bDot) {
    return dDot;
  }
  if (bDot != null) return bDot;
  return dDot ?? "Good Standing";
});

const readinessChipKey = computed(() => {
  const id =
    displayBadge.value?.id ??
    props.readinessChip?.id ??
    props.employee?.badge?.id ??
    "n";
  return `chip-${id}-${readinessChipLevel.value}-${readinessChipDot.value}`;
});

const readinessBadgeExpirationDate = computed(
  () => props.employee?.badge?.expiration_date ?? null
);
const readinessBadgeExpiresInDaysFromObsolete = computed(
  () => props.employee?.badge?.expires_in_days_from_becoming_obsolete ?? null
);
const readinessBadgeHasExpirationIfObsolete = computed(
  () => !!props.employee?.badge?.has_expiration_if_obsolete
);

/** Same source as readiness chip — required for remove dialog + GrandBadgeDropdown. */
const currentBadgeIdForDropdown = computed(() => {
  const snap = props.readinessChip;
  const d = displayBadge.value;
  const snapLevel =
    snap != null && typeof snap === "object" && snap.level != null && snap.level !== ""
      ? normalizeProficiencyLevelForChip(snap.level)
      : null;
  const dLevel =
    d?.level != null && d.level !== ""
      ? normalizeProficiencyLevelForChip(d.level)
      : null;
  if (
    dLevel != null &&
    snapLevel != null &&
    proficiencyRank(dLevel) > proficiencyRank(snapLevel) &&
    d?.id != null &&
    d.id !== ""
  ) {
    return d.id;
  }
  if (
    snap != null &&
    typeof snap === "object" &&
    snap.id != null &&
    snap.id !== ""
  ) {
    return snap.id;
  }
  return props.employee?.badge?.id ?? d?.id ?? null;
});

watch(
  () => props.awardDialogRequest,
  (req) => {
    if (!req?.token) return;
    nextTick(() => {
      if (!props.open) return;
      const lv = normalizeBadgeLevel(String(req.level || "apprentice"));
      awardBadgeLevel.value =
        LEVEL_TO_INT[lv] !== undefined ? lv : "apprentice";
      showAwardBadgeDialog.value = true;
    });
  },
  { deep: true }
);

watch(() => props.open, (val) => {
  if (!val) {
    showCheckpointView.value = false;
    localCurrentCheckpoint.value = 0;
    displayBadge.value = null;
    showAwardBadgeDialog.value = false;
    closeChatterDrawer();
  } else {
    mirrorEmployeeBadgeIntoDisplay();
  }
});

watch(
  () => props.employee,
  () => {
    mirrorEmployeeBadgeIntoDisplay();
  },
  { deep: true, immediate: true }
);

watch(
  () => props.readinessChip,
  () => {
    mirrorEmployeeBadgeIntoDisplay();
  },
  { deep: true, immediate: true }
);

watch(
  activeCheckpoints,
  (list) => {
    if (!list.length) {
      localCurrentCheckpoint.value = 0;
      return;
    }
    if (localCurrentCheckpoint.value >= list.length) {
      localCurrentCheckpoint.value = list.length - 1;
    }
  }
);

const onFilterChanged = (val) => {
  localHasFilter.value = val;
};

const onFilteredCheckpointIdsChanged = (ids) => {
  filteredCheckpointIds.value = ids;
};

/**
 * Full assignment dropdown only when role + position exist (Path to Readiness).
 * Responsibility-only rows (no role on the assignment) use OrgObjectCard — no empty dropdown.
 */
const isResponsibilityAssignment = computed(() => {
  const ra = props.responsibilityAssignment;
  return !!(
    ra?.responsibilityCode &&
    ra?.roleCode &&
    ra?.positionCode
  );
});

const employeeInitials = computed(() => {
  const name = props.employee?.fullName;
  if (!name) return "";
  return name
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");
});

const onRemoveBadge = (event) => {
  const badge = props.employee?.badge;
  const idFromEvent =
    event &&
    typeof event === "object" &&
    event.id != null &&
    event.id !== ""
      ? event.id
      : null;
  if (event && typeof event === "object" && idFromEvent != null) {
    emit("removeBadge", event);
    return;
  }
  const resolvedId = idFromEvent ?? currentBadgeIdForDropdown.value;
  emit("removeBadge", {
    id: resolvedId,
    level:
      typeof event === "string"
        ? event
        : event?.level ?? badge?.level ?? readinessChipLevel.value,
    currentBadge:
      badge ??
      (resolvedId != null && resolvedId !== ""
        ? { id: resolvedId, level: readinessChipLevel.value }
        : undefined),
    ...(typeof event === "object" && event ? event : {}),
  });
};

const isLastCheckpoint = computed(
  () =>
    activeCheckpoints.value.length > 0 &&
    localCurrentCheckpoint.value === activeCheckpoints.value.length - 1
);

const switchToTableAndShowNotYetPassed = () => {
  showCheckpointView.value = false;
  switchToTableWithNotYetPassedFilterCount.value += 1;
};

const onNotApplicableFromNavigator = (reason) => {
  const item = { ...currentCheckpointItem.value, reason };
  emit("onNotApplicable", item);
  if (isLastCheckpoint.value) {
    switchToTableAndShowNotYetPassed();
  }
};

const handleOnFail = (reason) => {
  emit("onFail", { ...currentCheckpointItem.value, reason });
  if (isLastCheckpoint.value) {
    switchToTableAndShowNotYetPassed();
  }
};

const onShowChecklistInspectionTab = () => {
  showCheckpointView.value = false;
  switchToTableWithNotYetPassedFilterCount.value += 1;
};

const handleOnPass = () => {
  const item = currentCheckpointItem.value;
  emit("onPass", item);
  // Last checkpoint: return to the checklist table in the right pane.
  if (isLastCheckpoint.value) {
    switchToTableAndShowNotYetPassed();
    return;
  }
  // Default "Not Yet Passed" filter drops the passed row from activeCheckpoints after reload.
  // Keeping the same index then shows the next remaining checkpoint. Incrementing as well
  // would skip one (looks like Pass jumps 2 checkpoints). Only advance when unfiltered.
  if (localHasFilter.value) {
    return;
  }
  localCurrentCheckpoint.value++;
};

const toDisplayLevel = (val) =>
  val ? String(val).charAt(0).toUpperCase() + String(val).slice(1).toLowerCase() : "Novice";

const onAwardBadge = async (isNotify) => {
  const newLevel = toDisplayLevel(awardBadgeLevel.value);
  try {
    proxy.$loader.show();
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    const res = await createProficiencyBadge(accountId, {
      employee: props.employee?.id,
      responsibility: props.responsibilityAssignment?.responsibilityId,
      level: LEVEL_TO_INT[awardBadgeLevel.value] ?? 1,
      given: false,
      issued_method: "awarded",
      notify: isNotify,
    });
    const newId = extractCreatedProficiencyBadgeId(res);
    proxy.$notifications?.success(
      "Badge awarded successfully",
      "",
      AWARD_SUCCESS_TOAST_MS
    );
    showAwardBadgeDialog.value = false;
    displayBadge.value = {
      level: newLevel,
      dot: "Good Standing",
      ...(newId != null && newId !== "" ? { id: newId } : {}),
    };
    emit("badgeAwarded", {
      level: newLevel,
      dot: "Good Standing",
      employeeId: props.employee?.id,
      responsibilityId: props.responsibilityAssignment?.responsibilityId,
      badgeId: newId,
    });
  } catch (e) {
    console.error("Error awarding badge:", e);
    proxy.$notifications?.error?.(
      "Unable to award the badge. Please try again."
    );
  } finally {
    proxy.$loader.hide();
  }
};

const onInspect = (item) => {
  const idx = activeCheckpoints.value.findIndex(
    (cp) => cp.id === item?.checkpoint_id
  );
  localCurrentCheckpoint.value = idx >= 0 ? idx : 0;
  showCheckpointView.value = true;
};

const onIssueBadge = (event) => {
  if (event?.isRemoveAction) {
    const id = event.id ?? currentBadgeIdForDropdown.value;
    emit("removeBadge", {
      ...event,
      id,
      level: event.level ?? event.label ?? readinessChipLevel.value,
      currentBadge:
        event.currentBadge ??
        (id != null && id !== ""
          ? { id, level: readinessChipLevel.value }
          : undefined),
    });
  } else {
    emit("issueBadge", event);
  }
};
</script>

<style scoped>
.checklist-inspection-right-pane {
  display: contents;
}

.checklist-inspection-right-pane-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 100;
}

.checklist-inspection-right-pane-wrapper {
  position: fixed;
  top: 0;
  right: 0;
  width: 80%;
  max-width: 80vw;
  height: 100vh;
  background: #fff;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
  z-index: 101;
  display: flex;
  flex-direction: column;
}

.checklist-inspection-right-pane-header-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0px 24px;
    padding-top: 31px;
    padding-bottom: 7px;
}

.checklist-inspection-right-pane-header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.checklist-inspection-right-pane-header-title {
  font-size: 1.125rem;
  font-weight: 600;
}

.checklist-inspection-right-pane-body {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.checklist-inspection-right-pane-body > * {
  height: 100%;
  overflow-y: auto;
}

/* Overlay fade (Vue 2: enter/leave, Vue 3: enter-from/leave-to) */
.checklist-inspection-pane-overlay-enter-active,
.checklist-inspection-pane-overlay-leave-active {
  transition: opacity 0.25s ease;
}

.checklist-inspection-pane-overlay-enter,
.checklist-inspection-pane-overlay-enter-from,
.checklist-inspection-pane-overlay-leave-to {
  opacity: 0;
}

/* Pane slide from right (Vue 2: enter/leave, Vue 3: enter-from/leave-to) */
.checklist-inspection-pane-slide-enter-active,
.checklist-inspection-pane-slide-leave-active {
  transition: transform 0.3s ease;
}

.checklist-inspection-pane-slide-enter,
.checklist-inspection-pane-slide-enter-from,
.checklist-inspection-pane-slide-leave-to {
  transform: translateX(100%);
}

.inspection-employee-card {
  display: flex;
  align-items: center;
  gap: 0px;
  margin-left: 16px;
}

.avatar-placeholder {
  color: #7f56d9;
  font-size: 12px;
  height: 32px;
  width: 32px;
  font-weight: 500;
  background-color: #f9f5ff;
  border-radius: 200px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.inspection-employee-card-info-name {
  color: #101828;
  font-size: 16px;
  font-weight: 600;
  line-height: 32px;
  margin-left: 5px;
  margin-right: 7px;
}
.inspection-employee-card-info-position {
  color: #475467;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
}
.checklist-inspection-right-pane-close-container {
    margin-right: 24px;
}
.checklist-inspection-right-pane-responsibility-header {
    padding: 12px 16px;
    background: linear-gradient(180deg, #A5C0EE 0%, #FBC5EC 100%);
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    align-self: stretch;
}

:deep(.rad__trigger-btn) {
  width: 100%;
}

.checklist-inspection-right-pane-responsibility-card {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  background: white !important;
  border: 1px solid #D0D5DD;
  border-radius: 8px !important;
  padding: 8px 12px !important;
  height: 44px;
  display: flex;
  align-items: center;
  box-shadow: 0px 1px 2px 0px #1018280D;
} 
.checklist-inspection-right-pane-responsibility-header-text {
  color: #101828;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 24px;
  margin-left: 0.5rem;
  white-space: nowrap;
  flex-shrink: 0;
}

.checklist-inspection-right-pane-responsibility-dropdown {
  flex: 1;
  min-width: 0;
}

.checkpoint-slide-enter-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.checkpoint-slide-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.checkpoint-slide-enter-from {
  transform: translateX(100%);
  opacity: 0;
}
.checkpoint-slide-enter-to {
  transform: translateX(0);
  opacity: 1;
}
.checkpoint-slide-leave-from {
  transform: translateX(0);
  opacity: 1;
}
.checkpoint-slide-leave-to {
  transform: translateX(-30%);
  opacity: 0;
}

::v-deep(.org-object-card-compass) {
  max-width: 100% !important;
}
</style>

<!-- Global: v-dialog content is teleported; must sit above pane overlay (z-index 100–101). -->
<style>
.checklist-inspection-award-dialog {
  z-index: 300 !important;
}
</style>
